import { useState, useCallback } from "react";
import { getTemplateAttachments, uploadTemplateAttachment } from "../../../../api/v2/TasklistsApi";

export interface TemplateAttachmentsControllerProps {
    phaseTypeId: number;
    selectedFiles: string[];
    onFilesChanged: (files: string[]) => void;
    canEdit: boolean;
}

export interface TemplateAttachmentsController {
    files: string[];
    canEdit: boolean;
    onUploadFile: (file: File) => Promise<void>;
    onRemoveFile: (filename: string) => void;
    showRemoveConfirm: string | null;
    setShowRemoveConfirm: (filename: string | null) => void;
    pendingOverwrite: File | null;
    confirmOverwrite: () => Promise<void>;
    cancelOverwrite: () => void;
}

export function useTemplateAttachmentsController({
    phaseTypeId,
    selectedFiles,
    onFilesChanged,
    canEdit,
}: TemplateAttachmentsControllerProps): TemplateAttachmentsController {
    const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);
    const [pendingOverwrite, setPendingOverwrite] = useState<File | null>(null);

    const doUpload = useCallback(async (file: File) => {
        const result = await uploadTemplateAttachment(phaseTypeId, file);
        if (result?.data?.filename) {
            if (!selectedFiles.includes(result.data.filename)) {
                onFilesChanged([...selectedFiles, result.data.filename]);
            }
        }
    }, [phaseTypeId, selectedFiles, onFilesChanged]);

    const onUploadFile = useCallback(async (file: File) => {
        const existing = await getTemplateAttachments(phaseTypeId);
        if (existing?.data?.includes(file.name)) {
            setPendingOverwrite(file);
            return;
        }
        await doUpload(file);
    }, [phaseTypeId, doUpload]);

    const confirmOverwrite = useCallback(async () => {
        if (pendingOverwrite) {
            await doUpload(pendingOverwrite);
            setPendingOverwrite(null);
        }
    }, [pendingOverwrite, doUpload]);

    const cancelOverwrite = useCallback(() => {
        setPendingOverwrite(null);
    }, []);

    const onRemoveFile = useCallback((filename: string) => {
        onFilesChanged(selectedFiles.filter(f => f !== filename));
        setShowRemoveConfirm(null);
    }, [selectedFiles, onFilesChanged]);

    return {
        files: selectedFiles,
        canEdit,
        onUploadFile,
        onRemoveFile,
        showRemoveConfirm,
        setShowRemoveConfirm,
        pendingOverwrite,
        confirmOverwrite,
        cancelOverwrite,
    };
}
